// keyrd_console/src/components/RiskSafetyPanel.js
import React, { useEffect, useState } from "react";
import { buildKeyrdHeaders } from "../api/headers";

function boolLabel(v) {
  return v ? "Yes" : "No";
}

function RiskSafetyPanel({ auth }) {
  const { baseUrl, token, authorized, registryVersion, registryHash } = auth;

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const [safety, setSafety] = useState({});
  const [freezes, setFreezes] = useState([]);
  const [violations, setViolations] = useState([]);

  useEffect(() => {
    if (!authorized || !baseUrl) return;

    const ac = new AbortController();

    setLoading(true);
    setErr(null);

    const headers = buildKeyrdHeaders({
      token,
      registryVersion,
      registryHash,
      extra: { Accept: "application/json" },
    });

    fetch(`${baseUrl}/console/safety`, { headers, signal: ac.signal })
      .then(async (res) => {
        const text = await res.text();
        let json;

        try {
          json = text ? JSON.parse(text) : null;
        } catch {
          throw new Error(`Non-JSON response: ${text.slice(0, 120)}…`);
        }

        if (!res.ok || !json) {
          const msg = json?.error || json?.reason || `HTTP ${res.status}`;
          throw new Error(msg);
        }

        const safetyBlock = json.safety || {};
        const freezesBlock = Array.isArray(json.freezes) ? json.freezes : [];
        const violationsBlock = Array.isArray(json.violations) ? json.violations : [];

        setSafety(safetyBlock);
        setFreezes(freezesBlock);
        setViolations(violationsBlock);
      })
      .catch((e) => {
        if (e?.name === "AbortError") return;
        setErr(e?.message || String(e));
      })
      .finally(() => {
        if (!ac.signal.aborted) setLoading(false);
      });

    return () => ac.abort();
  }, [authorized, baseUrl, token, registryVersion, registryHash]);

  const safetyFreezeActive =
    safety.safety_freeze_active ??
    safety.safety_freeze ??
    false;

  const technicalFreezeActive =
    safety.technical_freeze_active ??
    safety.technical_freeze ??
    false;

  const registryMismatch =
    safety.registry_mismatch ??
    safety.registry_in_sync === false ??
    false;

  const serverRegistryHash =
    safety.server_registry_hash || safety.backend_registry_hash;

  const safeSetStatus =
    safety.safe_nudge_set_status ||
    (safety.safe_nudge_set_active ? "Active" : "Unknown");

  const lastViolationAt = safety.last_violation_at || null;

  return (
    <div>
      <h1 style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>
        Risk &amp; Safety
      </h1>
      <p style={{ fontSize: 13, color: "#4b5563", marginBottom: 16 }}>
        Freeze-aware safety snapshot for the current KeyRD pilot{" "}
        <span style={{ color: "#6b7280" }}>
          (powered by <code>/console/safety</code>).
        </span>
      </p>

      {!authorized && (
        <div
          style={{
            padding: 10,
            borderRadius: 8,
            border: "1px solid #f97316",
            backgroundColor: "#fffbeb",
            fontSize: 13,
          }}
        >
          Paste a valid RDN Console token in the top bar to load safety
          information.
        </div>
      )}

      {authorized && loading && <div>Loading safety status…</div>}

      {authorized && err && (
        <div
          style={{
            marginTop: 8,
            padding: 8,
            borderRadius: 6,
            border: "1px solid #fecaca",
            backgroundColor: "#fef2f2",
            fontSize: 12,
            color: "#b91c1c",
          }}
        >
          Error loading safety panel: {err}
        </div>
      )}

      {authorized && !loading && !err && (
        <>
          {/* Summary cards */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 16,
              marginTop: 16,
              marginBottom: 24,
            }}
          >
            <SummaryCard
              title="Safety freeze active"
              value={boolLabel(safetyFreezeActive)}
              tone={safetyFreezeActive ? "bad" : "good"}
              subtitle={
                safetyFreezeActive
                  ? safety.safety_freeze_reason || "See active freezes below."
                  : "No active clinical safety freeze."
              }
            />
            <SummaryCard
              title="Technical freeze active"
              value={boolLabel(technicalFreezeActive)}
              tone={technicalFreezeActive ? "warn" : "good"}
              subtitle={
                technicalFreezeActive
                  ? safety.technical_freeze_reason ||
                    "Technical inconsistency detected."
                  : "Controller and registry currently unfrozen."
              }
            />
            <SummaryCard
              title="Registry / hash mismatch"
              value={boolLabel(registryMismatch)}
              tone={registryMismatch ? "warn" : "good"}
              subtitle={
                registryMismatch
                  ? "Backend registry does not match client."
                  : "Registry hash in sync for this pilot."
              }
            />
            <SummaryCard
              title="Safe Nudge Set"
              value={safeSetStatus}
              tone={safeSetStatus === "Active" ? "good" : "neutral"}
              subtitle={
                safety.ed_risk_safe_set
                  ? "ED-risk safe set is enforced."
                  : "ED risk state unknown from this endpoint."
              }
            />
          </div>

          {/* Registry + controller info */}
          <section
            style={{
              marginBottom: 24,
              padding: 14,
              borderRadius: 10,
              backgroundColor: "#ffffff",
              boxShadow: "0 1px 2px rgba(15,23,42,0.05)",
              border: "1px solid #e5e7eb",
              fontSize: 12,
            }}
          >
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 16,
                marginBottom: 8,
              }}
            >
              <InfoRow
                label="Controller version"
                value={safety.controller_version || "unknown"}
              />
              <InfoRow
                label="Registry version"
                value={safety.registry_version || "1.0.0"}
              />
              <InfoRow
                label="Last violation"
                value={lastViolationAt || "none recorded"}
              />
            </div>

            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 16,
                marginTop: 8,
                color: "#4b5563",
              }}
            >
              <InfoRow
                label="Client registry hash"
                value={registryHash || "n/a"}
                mono
              />
              <InfoRow
                label="Backend registry hash"
                value={serverRegistryHash || "n/a"}
                mono
              />
            </div>
          </section>

          {/* Active freezes */}
          <section style={{ marginBottom: 24 }}>
            <SectionTitle>Active freezes</SectionTitle>
            {freezes && freezes.length > 0 ? (
              <table
                style={{
                  width: "100%",
                  marginTop: 8,
                  borderCollapse: "collapse",
                  fontSize: 12,
                  backgroundColor: "#ffffff",
                  borderRadius: 10,
                  overflow: "hidden",
                  boxShadow: "0 1px 2px rgba(15,23,42,0.05)",
                  border: "1px solid #e5e7eb",
                }}
              >
                <thead>
                  <tr
                    style={{
                      textAlign: "left",
                      backgroundColor: "#f9fafb",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    <th style={{ padding: "6px 8px" }}>Started</th>
                    <th style={{ padding: "6px 8px" }}>Kind</th>
                    <th style={{ padding: "6px 8px" }}>Scope</th>
                    <th style={{ padding: "6px 8px" }}>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {freezes.map((f, idx) => (
                    <tr key={f.id || idx} style={{ borderBottom: "1px solid #f3f4f6" }}>
                      <td style={{ padding: "6px 8px" }}>
                        {f.started_at || f.created_at || "-"}
                      </td>
                      <td style={{ padding: "6px 8px" }}>
                        {f.kind || f.freeze_kind || "-"}
                      </td>
                      <td style={{ padding: "6px 8px" }}>
                        {f.scope || f.scope_kind || "-"}
                      </td>
                      <td style={{ padding: "6px 8px" }}>
                        {f.reason || f.message || "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <EmptyHint>
                No active safety or technical freezes for this pilot.
              </EmptyHint>
            )}
          </section>

          {/* Violations table */}
          <section>
            <SectionTitle>Recent safety violations</SectionTitle>
            {violations && violations.length > 0 ? (
              <table
                style={{
                  width: "100%",
                  marginTop: 8,
                  borderCollapse: "collapse",
                  fontSize: 12,
                  backgroundColor: "#ffffff",
                  borderRadius: 10,
                  overflow: "hidden",
                  boxShadow: "0 1px 2px rgba(15,23,42,0.05)",
                  border: "1px solid #e5e7eb",
                }}
              >
                <thead>
                  <tr
                    style={{
                      textAlign: "left",
                      backgroundColor: "#f9fafb",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    <th style={{ padding: "6px 8px" }}>Time</th>
                    <th style={{ padding: "6px 8px" }}>Rule</th>
                    <th style={{ padding: "6px 8px" }}>Severity</th>
                    <th style={{ padding: "6px 8px" }}>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {violations.map((v, idx) => (
                    <tr key={v.id || idx} style={{ borderBottom: "1px solid #f3f4f6" }}>
                      <td style={{ padding: "6px 8px" }}>
                        {v.occurred_at || v.created_at || "-"}
                      </td>
                      <td style={{ padding: "6px 8px" }}>
                        {v.rule || v.kind || "-"}
                      </td>
                      <td style={{ padding: "6px 8px" }}>
                        {v.severity || v.level || "-"}
                      </td>
                      <td style={{ padding: "6px 8px" }}>
                        {v.description || v.message || "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <EmptyHint>
                No logged safety violations yet. Controller has not recorded any
                tone, scope, or ED-risk breaches for this pilot.
              </EmptyHint>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function SummaryCard({ title, value, subtitle, tone = "neutral" }) {
  let border = "#e5e7eb";
  let bg = "#ffffff";
  let valueColor = "#111827";

  if (tone === "good") {
    border = "#bbf7d0";
    bg = "#ecfdf5";
    valueColor = "#166534";
  } else if (tone === "bad") {
    border = "#fecaca";
    bg = "#fef2f2";
    valueColor = "#b91c1c";
  } else if (tone === "warn") {
    border = "#fed7aa";
    bg = "#fffbeb";
    valueColor = "#92400e";
  }

  return (
    <div
      style={{
        minWidth: 220,
        padding: 14,
        borderRadius: 10,
        backgroundColor: bg,
        boxShadow: "0 1px 2px rgba(15,23,42,0.05)",
        border: `1px solid ${border}`,
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
        {title}
      </div>
      <div
        style={{
          fontSize: 20,
          fontWeight: 600,
          marginBottom: 4,
          color: valueColor,
        }}
      >
        {value}
      </div>
      {subtitle && (
        <div style={{ fontSize: 11, color: "#6b7280" }}>{subtitle}</div>
      )}
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <h2
      style={{
        fontSize: 14,
        fontWeight: 600,
        marginBottom: 4,
        marginTop: 4,
      }}
    >
      {children}
    </h2>
  );
}

function EmptyHint({ children }) {
  return (
    <div
      style={{
        marginTop: 8,
        padding: 10,
        borderRadius: 8,
        border: "1px dashed #d1d5db",
        backgroundColor: "#f9fafb",
        fontSize: 12,
        color: "#6b7280",
      }}
    >
      {children}
    </div>
  );
}

function InfoRow({ label, value, mono = false }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: "#6b7280" }}>{label}</div>
      <div
        style={{
          fontSize: 12,
          fontFamily: mono
            ? "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace"
            : "inherit",
          wordBreak: "break-all",
        }}
      >
        {value}
      </div>
    </div>
  );
}

export default RiskSafetyPanel;
